import { Box, Heading, Stack, Text } from '@chakra-ui/react'
import { groupBy, sum } from 'lodash-es'
import 'swiper/swiper-bundle.css'

import UserQ from '@/queries/UserQ'
import UserVolunteerRoleQ from '@/queries/UserVolunteerRoleQ'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'
import {
  ACHIEVEMENT_CATEGORY_TO_LABEL,
  ACHIEVEMENT_ID_TO_DATA,
  type AchievementCategory,
  getSortedAchievements,
  PROGRESSION_DEFAULT,
} from '@/client/services/progression'

import BadgeList from '@/components/Achievements/BadgeList'

const CATEGORIES_TO_SHOW: AchievementCategory[] = ['attend', 'certify', 'host', 'refer']

export default function ProfileBadgesFull({ user }: { user: Query }): JSX.Element | null {
  const currentUser = useUser()
  const userId = user(UserQ.id)

  const userName = user(UserQ.display_name)
  const userVolunteerRoles = user.many(UserVolunteerRoleQ.where({ userId: UserQ.id }))
  const progression = user(UserQ.progression) ?? PROGRESSION_DEFAULT
  const achievementIds = getSortedAchievements({
    progression,
    completed: currentUser.id === userId ? null : true,
  })

  const groupedAchievementIds = groupBy(
    achievementIds,
    achievementId => ACHIEVEMENT_ID_TO_DATA[achievementId].category
  )

  const specialBadgesCount =
    (groupedAchievementIds['one-off'] ?? []).length + userVolunteerRoles.length
  const pointsBadgesCount = (groupedAchievementIds.level ?? []).length

  const totalAchievementsShown = sum(
    CATEGORIES_TO_SHOW.map(c => groupedAchievementIds[c]?.length ?? 0)
  )

  return (
    <Box>
      <Stack direction="column" spacing="40px">
        {pointsBadgesCount === 0 ? null : (
          <Box>
            <Heading size="xl" as="h2" mb="24px">
              Schoolhouse Points
            </Heading>
            <BadgeList
              userVolunteerRoles={[]}
              achievementIds={groupedAchievementIds.level ?? []}
              progression={progression}
            />
          </Box>
        )}
        {specialBadgesCount === 0 ? null : (
          <Box>
            <Heading size="xl" as="h2" mb="24px">
              Special Badges
            </Heading>
            <BadgeList
              userVolunteerRoles={userVolunteerRoles}
              achievementIds={groupedAchievementIds['one-off'] ?? []}
              progression={progression}
            />
          </Box>
        )}
        <Box>
          <Heading size="xl" as="h2" mb="32px">
            Achievements
          </Heading>
          {totalAchievementsShown === 0 ? (
            <Text>{userName} has no achievements yet!</Text>
          ) : (
            <Stack direction="column" spacing="32px">
              {CATEGORIES_TO_SHOW.map(category =>
                (groupedAchievementIds[category] ?? []).length === 0 ? null : (
                  <Box key={category}>
                    <Text variant="eyebrow" mb="12px">
                      {ACHIEVEMENT_CATEGORY_TO_LABEL[category]}
                    </Text>
                    <BadgeList
                      key={category}
                      userVolunteerRoles={[]}
                      achievementIds={groupedAchievementIds[category] ?? []}
                      progression={progression}
                    />
                  </Box>
                )
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  )
}
